import {useCallback} from 'react';
import {stringToUint8Array} from "@common/hooks/livekit/useLiveKitEventListener";
import { useRoomContext } from "@livekit/components-react"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import { toLiveTodoPayload } from "../../../engine/live/room/liveTodoPayload"

// LiveKit DataChannel hard limit is 65535 bytes. Browsers/SFU reject anything
// above that with TypeError "Message too large", which closes the underlying
// PeerConnection. Cap below the limit to keep headroom for envelope overhead.
const MAX_PAYLOAD_BYTES = 32 * 1024
// Body size used when chunking. Kept below the cap so the URI-encoded JSON
// envelope (type, chunk_id, chunk_index, chunk_total, chunk_payload) plus the
// encodeURIComponent inflation of the payload slice still fit under the cap.
const CHUNK_BODY_BYTES = 12 * 1024
// Backoff schedule (ms) for transient publish failures. Pre-dates this file's
// empty-destination retry (PR #654) — both delay paths share the same known
// limitation: a delayed send has no sequence guarantee against a later call
// that goes through immediately, so a stale message can in theory land after
// a fresher one for the same recipient (e.g. two sound-mode toggles in
// quick succession). Fixing this needs an application-level ordering/ack,
// which is the structural decision tracked separately (ack vs REST fallback)
// — not a per-caller patch here.
const RETRY_DELAYS_MS = [500, 1000, 2000]
const CHUNK_TYPE = "__chunk"

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const isTransientPublishError = (error) => {
    const message = (error?.message || error?.toString() || "").toLowerCase()
    return (
        message.includes("engine closed") ||
        message.includes("pc manager is closed") ||
        message.includes("pc manager closed") ||
        message.includes("message too large")
    )
}

const randomChunkId = () => {
    // Lightweight unique id, sufficient to disambiguate concurrent chunked
    // transmissions on the receiver. Not security-sensitive.
    return (
        Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
    )
}

const useLiveKitEventSender = () => {

    const room = useRoomContext();

    if (!room) {
        return {sendLiveKitData: () => Promise.resolve({ok: false, reason: 'no_room'})};
    }

    // Single attempt to publish bytes to LiveKit.
    const publishOnce = async (encodedData, destinationIdentities) => {
        await room.localParticipant.publishData(encodedData, {
            reliable: true,
            destinationIdentities,
        })
        return true
    }

    // Publish with retry-on-transient-error. We do NOT force-reconnect here
    // (that path is owned by useGhostParticipantRecovery). On the final
    // failure we log to Bugsnag once so the cluster shrinks but we still keep
    // a trace.
    const publishWithRetry = async (
        encodedData,
        destinationIdentities,
        context
    ) => {
        let lastError = null
        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            if (!room || room?.state === "disconnected") {
                notifyBugsnag(`LiveKit publishData aborted (${context?.type}): room disconnected mid-retry`)
                return false
            }
            try {
                await publishOnce(encodedData, destinationIdentities)
                return true
            } catch (error) {
                lastError = error
                if (!isTransientPublishError(error)) {
                    notifyBugsnag(
                        `LiveKit publishData failed (${context?.type}): ${error?.message || error}`
                    )
                    return false
                }
                const delay = RETRY_DELAYS_MS[attempt]
                if (delay === undefined) break
                await sleep(delay)
            }
        }
        notifyBugsnag(
            `LiveKit publishData gave up after retries (${context?.type}, bytes=${encodedData?.length}): ${lastError?.message || lastError}`
        )
        return false
    }

    // Resolves the LiveKit identities a logical destinationIds (user ids)
    // list maps to, reading room.remoteParticipants fresh each time it's
    // called — the mapping can change between a first check and a retry.
    const resolveDestinationIdentities = (destinationIds) => {
        const participants = [...(room?.remoteParticipants?.keys() ?? [])];
        return destinationIds?.length > 0 ? destinationIds?.reduce((pValue, cValue) => {
            const matchingIdentities = participants.filter(p => parseInt(p?.split(".")[0]) === cValue);
            return [
                ...pValue,
                ...matchingIdentities
            ]
        }, []) : null;
    }

    // resolveDestinationIdentities only ever returns null (broadcast) or an
    // array (possibly empty), so "empty target" is just "non-null array of
    // length 0" — no need for whatIsIt here.
    const isEmptyTarget = (destinationIdentities) =>
        Array.isArray(destinationIdentities) && destinationIdentities.length === 0

    const sendLiveKitData = useCallback(async (type, data, destinationIds = null) => {
        if (!room || room?.state === 'disconnected') {
            notifyBugsnag(`LiveKit sendLiveKitData skipped (${type}): room disconnected`)
            return {ok: false, reason: 'room_disconnected'}
        }

        const event = {...(type === "newTodo" ? toLiveTodoPayload(data) : data), type};
        // Use encodeURIComponent to properly handle special characters
        const jsonString = JSON.stringify(event);
        const encodedString = encodeURIComponent(jsonString);

        // Publishes the (already payload-sized) event to the given
        // identities, chunking if needed. Assumes destinationIdentities is
        // non-empty or null (broadcast) — the empty-target case is handled
        // by the caller before this runs.
        const publish = async (destinationIdentities) => {
            // Common case: payload fits a single DataChannel frame. Behaviour
            // is unchanged for old clients on the receiving end.
            if (encodedString.length <= MAX_PAYLOAD_BYTES) {
                const encodedData = stringToUint8Array(encodedString)
                const ok = await publishWithRetry(encodedData, destinationIdentities, { type })
                return {ok, reason: ok ? undefined : 'publish_failed'}
            }

            // Large payload: split the *raw* JSON string into < CHUNK_BODY_BYTES
            // slices and wrap each one into a __chunk envelope. The envelope is
            // then JSON.stringified + URI-encoded just like a regular event, so
            // the receiver path is uniform. The receiver reassembles in
            // useLiveKitEventHandler before dispatching to the original handler.
            const chunkId = randomChunkId()
            const totalChunks = Math.ceil(jsonString.length / CHUNK_BODY_BYTES)
            const slices = []
            for (let i = 0; i < totalChunks; i++) {
                slices.push(jsonString.slice(i * CHUNK_BODY_BYTES, (i + 1) * CHUNK_BODY_BYTES))
            }

            // Sequential send so the receiver gets chunks in order and the
            // DataChannel reliable queue never bloats. LiveKit's JS client
            // doesn't expose bufferedAmount uniformly across RN/web, so the
            // sequential await is our backpressure.
            for (let index = 0; index < slices.length; index++) {
                if (!room || room?.state === "disconnected") {
                    notifyBugsnag(`LiveKit sendLiveKitData aborted mid-chunk (${type}): room disconnected`)
                    return {ok: false, reason: 'room_disconnected'}
                }
                const envelope = {
                    type: CHUNK_TYPE,
                    chunk_id: chunkId,
                    chunk_index: index,
                    chunk_total: totalChunks,
                    chunk_payload: slices[index],
                }
                const envelopeStr = JSON.stringify(envelope)
                const encodedEnvelope = encodeURIComponent(envelopeStr)
                const ok = await publishWithRetry(
                    stringToUint8Array(encodedEnvelope),
                    destinationIdentities,
                    { type: `${type}#chunk` }
                )
                if (!ok) return {ok: false, reason: 'publish_failed'}
            }
            return {ok: true}
        }

        const destinationIdentities = resolveDestinationIdentities(destinationIds)

        if (!isEmptyTarget(destinationIdentities)) {
            return publish(destinationIdentities)
        }

        // Target filtered to empty: the destination user(s) aren't in
        // room.remoteParticipants right now (reconnect/refresh in flight,
        // typically). Give it one short retry before giving up — a lot of
        // these resolve within a second — then surface a typed failure so
        // the caller can tell the user, instead of staying silent.
        await sleep(500)
        const retried = resolveDestinationIdentities(destinationIds)
        if (isEmptyTarget(retried)) {
            notifyBugsnag(
                `LiveKit sendLiveKitData gave up (${type}): destination user(s) [${destinationIds}] not in remoteParticipants after retry`
            )
            return {ok: false, reason: 'destination_unreachable'}
        }
        return publish(retried)
    }, [room]);

    return {sendLiveKitData}

};

export default useLiveKitEventSender;
